import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { PawktLogo } from "@/components/pawkt/PawktLogo";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FormModal } from "@/components/pawkt/FormModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, MapPin, Trash2, ToggleRight, Gauge, ArrowUpRight, SlidersHorizontal, Clock, CreditCard, AlarmClock } from "lucide-react";
import { hasPerm } from "@/lib/perms";
import { PawktConnectSettings } from "@/components/pawkt/PawktConnectSettings";

function CollectionUrgencyCard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasPerm(user, "settings.edit");
  const { data } = useQuery({ queryKey: ["collection-urgency"], queryFn: async () => (await api.get("/settings/collection-urgency")).data });
  const [day, setDay] = useState("");
  useEffect(() => { if (data) setDay(String(data.day)); }, [data]);
  const save = useMutation({
    mutationFn: () => api.put("/settings/collection-urgency", { day: Number(day) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["collection-urgency"] }); qc.invalidateQueries({ queryKey: ["urgent-collections"] }); toast.success("Urgency date saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const n = Number(day);
  const valid = Number.isInteger(n) && n >= 1 && n <= 31;
  if (!data) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="collection-urgency-card">
      <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1"><AlarmClock className="h-5 w-5 text-slate-400" /> Collection priorities</h3>
      <p className="text-xs text-slate-500 mb-4">From this day each month, the dashboard's Urgent Collections lists this month's invoices that still have a balance. This is not an invoice due date. Months shorter than this day use their last day ({data.timezone}).</p>
      <Label className="text-xs text-slate-500">Monthly Collection Urgency Date</Label>
      <div className="mt-1 flex items-center gap-2">
        <Input type="number" min={1} max={31} value={day} disabled={!canEdit} onChange={(e) => setDay(e.target.value)} data-testid="collection-urgency-day" className="w-24 bg-white" />
        <span className="text-sm text-slate-500">of every month <span className="text-slate-400">(default {data.default})</span></span>
      </div>
      {!valid && <p className="mt-1 text-xs text-rose-600" data-testid="collection-urgency-error">Enter a day from 1 to 31.</p>}
      {canEdit ? (
        <Button onClick={() => save.mutate()} disabled={!valid || save.isPending || n === data.day} data-testid="collection-urgency-save" className="mt-4 bg-slate-900 text-white">Save</Button>
      ) : <p className="mt-3 text-xs text-slate-400" data-testid="collection-urgency-readonly">Only users who can edit settings can change this.</p>}
    </div>
  );
}

function BookingRequirementsCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["booking-requirements"], queryFn: async () => (await api.get("/settings/booking-requirements")).data });
  const save = useMutation({
    mutationFn: (requirements) => api.put("/settings/booking-requirements", { requirements }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["booking-requirements"] }); toast.success("Booking requirements saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return null;
  const LABELS = { boarding: "Boarding", daycare: "Daycare", grooming: "Grooming", vet: "Vet / Clinic", training: "Training", other: "Other services" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="booking-requirements-card">
      <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Booking requirements</h3>
      <p className="text-xs text-slate-500 mb-4">Turn on "Requires staff" for a service category to make a free, active staff member mandatory before a booking is saved or confirmed. Boarding always needs a room/kennel before confirmation.</p>
      <div className="divide-y divide-slate-100">
        {data.categories.map((c) => (
          <label key={c} className="flex items-center justify-between py-2.5 text-sm" htmlFor={`req-staff-${c}`}>
            <span className="text-slate-700">{LABELS[c] || c} <span className="text-xs text-slate-400">· Requires staff</span></span>
            <Switch id={`req-staff-${c}`} data-testid={`req-staff-${c}`} disabled={!data.can_edit || save.isPending}
              checked={!!data.requirements[c]?.requires_staff}
              onCheckedChange={(v) => save.mutate({ ...data.requirements, [c]: { requires_staff: v } })} />
          </label>
        ))}
      </div>
      {!data.can_edit && <p className="mt-3 text-xs text-slate-400" data-testid="booking-requirements-readonly">Only users who can edit settings can change this.</p>}
    </div>
  );
}

function OrderSoonSettingsCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["order-soon-settings"], queryFn: async () => (await api.get("/settings/order-soon")).data, retry: false });
  const [v, setV] = useState("");
  const save = useMutation({
    mutationFn: () => api.put("/settings/order-soon", { threshold_pct: Number(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["order-soon-settings"] }); qc.invalidateQueries({ queryKey: ["order-soon"] }); toast.success("Order Soon threshold saved"); setV(""); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="order-soon-settings-card">
      <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Order Soon threshold</h3>
      <p className="text-xs text-slate-500 mb-3">A product is flagged when its stock is strictly below this % of its stock target (default {data.default_pct}%). Applies to the whole business.</p>
      <div className="flex items-center gap-2">
        <Input type="number" min="1" max="100" className="w-28" placeholder={String(data.threshold_pct)} value={v} onChange={(e) => setV(e.target.value)} disabled={!data.can_edit} data-testid="order-soon-threshold" />
        <span className="text-sm text-slate-500">% · current {data.threshold_pct}%</span>
        {data.can_edit && <Button size="sm" onClick={() => save.mutate()} disabled={!(Number(v) > 0 && Number(v) <= 100) || save.isPending} data-testid="order-soon-threshold-save">Save</Button>}
      </div>
    </div>
  );
}

function OnlinePaymentsCard() {
  const qc = useQueryClient();
  const { data: conn } = useQuery({
    queryKey: ["payment-connection"],
    queryFn: async () => (await api.get("/payments/connection")).data,
  });
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("payments");
    if (p === "connected") { toast.success("Razorpay connected"); qc.invalidateQueries({ queryKey: ["payment-connection"] }); }
    else if (p === "error") toast.error("Razorpay connection could not be completed. Please try again.");
  }, []); // eslint-disable-line
  const status = conn?.status || "not_connected";
  const connected = !!conn?.connected;
  const LABEL = { not_connected: "Not Connected", pending: "Connecting", connected: "Connected", action_required: "Action Required", disabled: "Disabled", revoked: "Revoked" };
  const onConnect = async () => {
    try { const { data } = await api.get("/payments/oauth/razorpay/start"); window.location.assign(data.authorize_url); }
    catch (e) { toast.error(e?.response?.status === 503 ? "Razorpay Connect is being set up — available shortly." : apiErr(e)); }
  };
  const onDisconnect = async () => {
    try { await api.post("/payments/oauth/razorpay/disconnect"); qc.invalidateQueries({ queryKey: ["payment-connection"] }); toast.success("Razorpay disconnected"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5" data-testid="online-payments-card">
      <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1"><CreditCard className="h-5 w-5 text-slate-400" /> Online Payments</h3>
      <p className="text-sm text-slate-500 mb-4">Accept card/UPI payments from your customers directly into your own Razorpay account. Pawkt never holds your funds.</p>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-900 flex items-center justify-center text-white text-[11px] font-bold">Rzp</div>
          <div>
            <div className="font-medium text-slate-800">Razorpay</div>
            <div className="flex items-center gap-1.5 text-xs" data-testid="online-payments-status">
              <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-300"}`} />
              <span className={connected ? "text-emerald-600" : "text-slate-500"}>{LABEL[status] || status}</span>
              {connected && conn?.environment && <span className="text-slate-400">· {conn.environment}</span>}
              {connected && conn?.metadata?.business_name && <span className="text-slate-400">· {conn.metadata.business_name}</span>}
            </div>
          </div>
        </div>
        {connected ? (
          <button onClick={onDisconnect} data-testid="disconnect-razorpay-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Disconnect</button>
        ) : (
          <button onClick={onConnect} data-testid="connect-razorpay-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pawkt-primary-hover">Connect Razorpay</button>
        )}
      </div>
      {!connected && <p className="mt-2 text-xs text-slate-400" data-testid="online-payments-hint">Until Razorpay is connected, online customer payments stay unavailable — manual methods (cash/UPI/card) continue to work.</p>}
    </div>
  );
}


function BranchCapacityDialog({ branchId, open, onClose }) {
  const qc = useQueryClient();
  const [conc, setConc] = useState("");
  const { data } = useQuery({
    queryKey: ["cap-settings", branchId],
    queryFn: async () => (await api.get(`/branches/${branchId}/capacity-settings`)).data,
    enabled: open && !!branchId,
  });
  useEffect(() => { if (data) { setConc(data.parallel_capacity ?? ""); } }, [data]);
  const save = useMutation({
    mutationFn: () => api.put(`/branches/${branchId}/capacity-settings`, { parallel_capacity: conc === "" ? null : Number(conc) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["cap-settings", branchId] }); toast.success("Capacity settings saved"); onClose(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="branch-capacity-modal" className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Branch capacity</DialogTitle>
          <DialogDescription>Optional parallel service capacity used to measure capacity. Operating hours are managed in Business Settings → Operating Hours.</DialogDescription>
        </DialogHeader>
        <Link to="/settings/business" onClick={onClose} className="text-sm text-emerald-700 underline" data-testid="cap-hours-link">Edit operating hours</Link>
        {data && (
          <div className="space-y-2">
            <div className="pt-2">
              <Label className="text-xs text-slate-500">Parallel capacity</Label>
              <Input type="number" min="1" value={conc} onChange={(e) => setConc(e.target.value)} placeholder="e.g. 3" data-testid="cap-parallel" className="mt-1 h-8" />
              <p className="mt-1 text-[11px] text-slate-400">Number of parallel service lanes / capacity units used to calculate capacity. Leave blank if not configured. This is NOT the number of employees.</p>
            </div>
            <Button onClick={() => save.mutate()} data-testid="cap-save" className="mt-2 w-full bg-slate-900 text-white">Save</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const USAGE_LABELS = {
  users: "Team users", branches: "Branches", customers: "Customers", pets: "Pets",
  bookings_per_month: "Bookings this month", services: "Services", products: "Products",
};

const MODULES = [
  ["vet", "Veterinary"], ["boarding", "Boarding"], ["daycare", "Daycare"], ["training", "Training"],
  ["loyalty", "Loyalty"], ["marketing", "Marketing"], ["memberships", "Memberships"],
  ["packages", "Packages"], ["multi_branch", "Multi-branch"], ["ai", "Pawkt AI"],
  ["business_planning", "Business Planning"],
];

export default function Settings() {
  const { user, refreshMe } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState(user?.tenant?.name || "");
  const [branchOpen, setBranchOpen] = useState(false);
  const [capBranchId, setCapBranchId] = useState(null);

  const { data: tenant, isLoading } = useQuery({ queryKey: ["tenant"], queryFn: async () => (await api.get("/tenants/me")).data });
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  const { data: billing } = useQuery({ queryKey: ["billing-usage"], queryFn: async () => (await api.get("/billing/usage")).data });

  const saveTenant = useMutation({ mutationFn: (b) => api.put("/tenants/me", b), onSuccess: async () => { await refreshMe(); qc.invalidateQueries({ queryKey: ["tenant"] }); toast.success("Saved"); }, onError: (e) => toast.error(apiErr(e)) });
  const addBranch = useMutation({ mutationFn: (b) => api.post("/branches", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["branches"] }); setBranchOpen(false); toast.success("Branch added"); }, onError: (e) => toast.error(apiErr(e)) });
  const delBranch = useMutation({ mutationFn: (id) => api.delete(`/branches/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["branches"] }); toast.success("Deleted"); } });

  if (isLoading || !tenant) return <Loading />;
  const flags = tenant.feature_flags || {};
  const toggleFlag = (key, val) => saveTenant.mutate({ feature_flags: { ...flags, [key]: val } });

  return (
    <div>
      <PageHeader title="Settings" subtitle="Business profile, branches & modules" />
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4"><Building2 className="h-5 w-5 text-slate-400" /> Business profile</h3>
          <Label className="text-xs text-slate-500">Business name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" className="mt-1 bg-white" />
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-400">Currency</span><div className="font-medium">{tenant.currency}</div></div>
            <div><span className="text-slate-400">Timezone</span><div className="font-medium">{tenant.timezone}</div></div>
            <div><span className="text-slate-400">Plan</span><div className="font-medium capitalize">{tenant.subscription_plan}</div></div>
            <div><span className="text-slate-400">Types</span><div className="font-medium">{(tenant.business_types || []).length}</div></div>
          </div>
          <Button onClick={() => saveTenant.mutate({ name })} data-testid="save-settings" className="mt-4 bg-slate-900 text-white">Save changes</Button>
          <Link to="/settings/business" data-testid="open-business-settings" className="ml-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline">All business details &amp; hours <ArrowUpRight className="h-4 w-4" /></Link>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><MapPin className="h-5 w-5 text-slate-400" /> Branches</h3>
            <Button size="sm" onClick={() => setBranchOpen(true)} data-testid="add-branch-btn" className="bg-slate-900 text-white gap-1"><Plus className="h-4 w-4" /> Add</Button>
          </div>
          <div className="space-y-2">
            {(branches || []).map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div><div className="font-medium text-slate-800">{b.name} {b.is_primary && <span className="text-xs text-emerald-600">· primary</span>}</div><div className="text-xs text-slate-400">{b.city}</div></div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setCapBranchId(b.id)} data-testid={`branch-hours-${b.id}`} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md" title="Branch capacity"><Clock className="h-4 w-4" /></button>
                  {!b.is_primary && <button onClick={() => delBranch.mutate(b.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <OnlinePaymentsCard />
        <CollectionUrgencyCard />
        <BookingRequirementsCard />
        <OrderSoonSettingsCard />

        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5" data-testid="plan-usage-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Gauge className="h-5 w-5 text-slate-400" /> Plan &amp; usage
              <span className="ml-2 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-slate-600" data-testid="current-plan">{tenant.subscription_plan}</span>
            </h3>
            <Link to="/billing#plans" data-testid="upgrade-plan-btn" className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Upgrade Plan <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(USAGE_LABELS).map(([k, label]) => {
              const used = billing?.usage?.[k] ?? 0;
              const lim = billing?.limits?.[k] ?? -1;
              const unlimited = lim < 0;
              const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(lim, 1)) * 100));
              const near = !unlimited && lim > 0 && used / lim >= 0.8;
              return (
                <div key={k} className="rounded-lg border border-slate-200 px-3.5 py-3" data-testid={`usage-${k}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{label}</span>
                    <span className={`font-semibold ${near ? "text-amber-600" : "text-slate-800"}`} data-testid={`usage-value-${k}`}>{used}{unlimited ? "" : ` / ${lim}`}</span>
                  </div>
                  {unlimited ? (
                    <div className="mt-2 text-xs text-slate-400">Unlimited</div>
                  ) : (
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className={`h-full rounded-full ${near ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-slate-400">Reached a limit? Upgrade your plan to add more — your data is never removed.</p>
        </div>

        <PawktConnectSettings />

        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4"><ToggleRight className="h-5 w-5 text-slate-400" /> Modules & feature flags</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MODULES.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-3">
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <Switch checked={!!flags[key]} onCheckedChange={(v) => toggleFlag(key, v)} data-testid={`flag-${key}`} />
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-400">Turning a module on reveals it in the sidebar immediately.</p>
        </div>

        <a href="/settings/commercial-rules" data-testid="commercial-rules-link" className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5 flex items-center justify-between hover:border-slate-300 transition-colors">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100"><SlidersHorizontal className="h-5 w-5 text-slate-500" /></span>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Commercial Rules</h3>
              <p className="text-xs text-slate-400">Configure how packages, memberships, coupons, loyalty &amp; manual discounts stack — per tenant or per branch.</p>
            </div>
          </div>
          <ArrowUpRight className="h-5 w-5 text-slate-400" />
        </a>

        <div className="lg:col-span-2 flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white p-6 text-center" data-testid="about-pawkt-card">
          <PawktLogo variant="footer" subtitle="Pet Business OS" />
          <p className="mt-1 text-xs text-slate-400">Pawkt CRM · your complete pet-business operating system</p>
          <p className="text-[11px] text-slate-300">© 2026 Pawkt. All rights reserved.</p>
        </div>
      </div>

      <FormModal open={branchOpen} onOpenChange={setBranchOpen} title="Add Branch" testid="branch-modal"
        initial={{ city: "Bengaluru" }} submitLabel="Add branch"
        fields={[{ name: "name", label: "Branch name", full: true }, { name: "city", label: "City" }, { name: "phone", label: "Phone" }, { name: "address", label: "Address", full: true }]}
        onSubmit={(v) => addBranch.mutateAsync(v)} />

      <BranchCapacityDialog branchId={capBranchId} open={!!capBranchId} onClose={() => setCapBranchId(null)} />
    </div>
  );
}
